import React, { FC, useCallback, useEffect, useMemo } from 'react';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { Form, Input, Skeleton, Button } from 'src/common';
import classNames from 'classnames';
import { getUpdateCacheOnUpdateMpName, useLoadMp, useUpdateMpName } from 'src/models/matchingProfiles/hooks';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';

interface FormState {
  name: string;
}

export const EcPreview: FC = () => {
  const { t } = useTranslation();
  const [{ vars }] = useApiTenders();
  const activeMpId = useMemo(() => vars.profileId, [vars.profileId]);
  const [updateSavedSearchName] = useUpdateMpName();
  const { data: mp, loading } = useLoadMp(activeMpId);

  const [form] = Form.useForm();

  const savedSearchName = mp?.name || '';

  const onSubmit = useCallback(
    (values: FormState) => {
      const { name } = values;
      mp &&
        updateSavedSearchName({
          variables: { name, id: mp.id },
          update: getUpdateCacheOnUpdateMpName(mp.id),
          optimisticResponse: {
            __typename: 'Mutation',
            updateMatchingProfile: {
              __typename: 'MatchingProfile',
              ...mp,
              name
            }
          }
        }).catch(() => {
          form.setFields([
            {
              name: 'name',
              errors: [t('Common.unknownErrorDesc')]
            }
          ]);
        });
    },
    [mp, updateSavedSearchName, form, t]
  );

  const initialValue = useMemo((): FormState => {
    return { name: savedSearchName };
  }, [savedSearchName]);

  useEffect(() => {
    form.resetFields();
  }, [form]);

  const isLoading = loading;

  return isLoading ? (
    <Skeleton active loading />
  ) : (
    <>
      <h2 className={classNames(styles.title)}>{`${savedSearchName}`}</h2>
      {
        <Form initialValues={initialValue} form={form} onFinish={onSubmit}>
          <div className={styles.unit}>
            <>
              <Form.Item
                label={t('Common.name')}
                name={'name'}
                rules={[
                  {
                    required: true,
                    message: t('Common.inputRequired', { inputName: t('Tenders.monitoringProfile') })
                  },
                  { max: 30, message: t('Common.inputTooLong', { inputName: t('Tenders.monitoringProfile') }) }
                ]}
              >
                <Input data-type={'name'} type={'text'} placeholder={t('Common.name')} />
              </Form.Item>
              <Button type={'primary'} htmlType={'submit'}>
                {t('Common.rename')}
              </Button>
            </>
          </div>
        </Form>
      }
    </>
  );
};

export default EcPreview;
